import moment from 'moment';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import envGet from '../../Env/Get/EnvGet.support';
import { boralLogo } from './Assets';

const REACT_APP_BORAL_ABRIDGED_CONDITIONS_URL = envGet({ key: `REACT_APP_BORAL_ABRIDGED_CONDITIONS_URL` });

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Print pdf line
 * @param {string} text text to print
 * @param {Object} props text properties
 * @returns {Object} line structure
 */
const line = (text, props = {}) => [{ text, ...props }];
/**
 * Renders heading
 * @param {Object} heading Heading object to render
 * @returns {Object} Heading to be rendered
 */
const renderHeading = (heading) => line(heading.label, { style: `subheader` });
/**
 * DeliveryDocketPdf
 * @param {Object} docket The docket
 * @returns {void} void
 */
const DeliveryDocketPdf = async (docket, options) => {
  if (!docket) {
    return null;
  }

  const { legal } = docket;

  /**
   * getDocketStatus
   * @param {string} status Docket status
   * @returns {string} Plain english status
   */
  const getDocketStatus = (status) => {
    switch (status) {
      case `IN_PROGRESS`:
        return `IN PROGRESS`;
      case `COMPLETE`:
        return `COMPLETE`;
      default:
        return null;
    }
  };

  const content = [];

  docket.values.forEach((el, valueIndex) => {
    const columns = [];
    let isBody = false;
    content.push(renderHeading(el));
    el.rows.forEach((row) => {
      if (Array.isArray(row)) {
        const tableBody = row.map((column, i, arr) => {
          // const isDate = moment(column, moment.ISO_8601).isValid();
          return {
            alignment: (i === arr.length - 1 && i !== 0) ? `right` : `left`,
            // text: isDate ? moment(column).format(`DD/MM/YYYY`) : column,
            text: column,
          };
        });
        columns.push(tableBody);
        isBody = true;
      } else {
        content.push({ style: `insideHeader`, text: row.label });
        const tableBody = row.rows.map((rowEl) => {
          return rowEl.map((column, i, arr) => {
            // const isDate = moment(column, moment.ISO_8601).isValid();
            return {
              alignment: (i === arr.length - 1 && i !== 0) ? `right` : `left`,
              // text: isDate ? moment(column).format(`DD/MM/YYYY`) : column,
              text: column,
            };
          });
        });
        const widths = new Array(tableBody[0].length).fill(`*`);
        widths[tableBody[0].length - 1] = `auto`;
        content.push({
          layout: `noBorders`,
          style: `contentTable`,
          table: {
            body: tableBody,
            widths,
          },
        });
      }
    });

    if (isBody) {
      const widths = new Array(columns[0].length).fill(`*`);
      widths[columns[0].length - 1] = `auto`;
      content.push({
        layout: {
          hLineColor: () => `#ccc`,
          hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 0 : 1),
          vLineWidth: () => 0,
        },
        style: `contentTable`,
        table: {
          body: columns,
          widths,
        },
      });
    }
  });

  content.push({
    layout: `noBorders`,
    style: `contentTable`,
    table: {
      body: [
        [
          {
            text: `Abridged Conditions of Quotation and Supply - Concrete`,
            link: REACT_APP_BORAL_ABRIDGED_CONDITIONS_URL,
            decoration: `underline`,
          },
        ],
      ],
    },
  });

  const horizontalMargin = 35;
  const topMargin = 15;
  const logoWidth = 64;

  const dateUpdated = moment(docket.lastUpdated).format(`hh:mma, ddd DD MMM YYYY`);
  const downloadFilenameDateFormat = `DDMMYY`;
  const downloadFilename = `Docket${docket.docketId}_${moment(docket.ticketDate).format(downloadFilenameDateFormat)}.pdf`;

  const dd = {
    content,
    defaultStyle: {
      fontSize: 12,
    },
    footer: {
      columns: [
        {
          alignment: `left`,
          color: `#999999`,
          fontSize: 10,
          layout: `noBorders`,
          margin: [40, 15, 35, 0],
          table: {
            body: [
              line(`Report data shown for information purposes only. Please refer to your Boral Tax Invoice for final fees and charges`),
              line(`Information subject to change where applicable`),
            ],
            widths: [`*`],
          },
        },
      ],
    },
    header: {
      columns: [
        {
          alignment: `left`,
          image: boralLogo,
          margin: [horizontalMargin, topMargin, 0, 0],
          width: logoWidth,
        },
        {
          alignment: `left`,
          fontSize: 6,
          layout: `noBorders`,
          margin: [logoWidth - 20, topMargin - 3, 0, 0],
          table: {
            body: [
              line(legal.companyName, { bold: true }),
              line(legal.companyABN),
              line(legal.companyAddress1),
              line(legal.companyAddress2),
              line(legal.companyAddress3),
              line(legal.companyPhone),
            ].filter((row) => row !== ``),
            widths: [`*`],
          },
        },
        {
          alignment: `right`,
          layout: `noBorders`,
          margin: [0, topMargin, horizontalMargin, 0],
          style: `updatedOn`,
          table: {
            body: [
              [{ color: `#000000`, text: `DOCKET #${docket.docketId} (${getDocketStatus(docket.status)})` }],
              [
                {
                  fontSize: 8,
                  text: `UPDATED ON: ${dateUpdated}`,
                },
              ],
            ],
            widths: [`*`],
          },
        },
      ],
    },
    pageMargins: [40, 80, 35, 60],
    styles: {
      contentTable: {
        fontSize: 10,
        margin: [0, 5, 0, 15],
        padding: [0, 1, 0, 1],
      },
      header: {
        bold: true,
        fontSize: 14,
        margin: [0, 0, 0, 10],
      },
      insideHeader: {
        bold: true,
        fontSize: 12,
        margin: [0, 10, 0, 5],
      },
      subheader: {
        bold: true,
        fontSize: 16,
        margin: [0, 10, 0, 5],
      },
      tableHeader: {
        bold: true,
        color: `black`,
        fontSize: 13,
      },
      updatedOn: {
        color: `#999999`,
        fontSize: 10,
        margin: [0, 0, 0, 10],
      },
    },
  };

  // if blob is passed then its request for multiple pdf that will be zipped
  if (options && options.blob) {
    return new Promise((resolve, reject) => {
      pdfMake.createPdf(dd).getBlob(async (blob) => {
        resolve(blob);
      });
    });
  }

  pdfMake.createPdf(dd).download(downloadFilename);
};

export default DeliveryDocketPdf;
